import { useLazyQuery } from "@apollo/client"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import Country from "../models/country"
import Region from "../models/region"
import {
  listCountries,
  listRegions,
  listSubRegions,
} from "../services/graphql/queries"
import { logger, Status } from "../services/utilities/utility"
import { MainContext } from "./main"

interface CountriesContextInterface {
  loading: boolean
  regionsList: Region[]
  subRegionsList: Region[]
  countriesList: Country[]
}

const CountriesContext = createContext<CountriesContextInterface>({
  loading: true,
  regionsList: [],
  subRegionsList: [],
  countriesList: [],
})

const CountriesController = ({ children }: { children: ReactNode }) => {
  const { setError, setErrorMessage, updatePanelStatus } =
    useContext(MainContext)

  // loadings
  const [regionsLoading, setRegionsLoading] = useState<boolean>(true) // loading for regions
  const [subRegionsLoading, setSubRegionsLoading] = useState<boolean>(true) // loading for subregions
  const [countriesLoading, setCountriesLoading] = useState<boolean>(true) // loading for countries
  const loading = countriesLoading || regionsLoading || subRegionsLoading // global loading

  // states
  const [regionsList, setRegionsList] = useState<Region[]>([]) // all regions list
  const [subRegionsList, setSubRegionsList] = useState<Region[]>([]) // all subregions list
  const [countriesList, setCountriesList] = useState<Country[]>([]) // all countries list

  // queries
  const [listRegionsQuery] = useLazyQuery(listRegions)
  const [listSubRegionsQuery] = useLazyQuery(listSubRegions)
  const [listCountriesQuery] = useLazyQuery(listCountries)

  // get regions list
  const getRegionsList = useCallback(async () => {
    updatePanelStatus("Regions", { loading: true })

    try {
      logger(Status.Api, "QUERY regionsList", listRegions)
      const { data } = await listRegionsQuery({
        variables: { input: { limit: 1000 } },
      })
      logger(Status.Info, "regions list", data.regionsList.items)

      setRegionsList(data.regionsList.items)

      setRegionsLoading(false)
      updatePanelStatus("Regions", { status: "success", loading: false })
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `regionsList`, e.message)
      }
      setRegionsLoading(false)
      updatePanelStatus("Regions", { status: "error", loading: false })
    }
  }, [setError, setErrorMessage])

  // get subregions list
  const getSubRegionsList = useCallback(async () => {
    updatePanelStatus("SubRegions", { loading: true })

    try {
      logger(Status.Api, "QUERY subregionsList", listSubRegions)
      const { data } = await listSubRegionsQuery({
        variables: { input: { limit: 1000 } },
      })
      logger(Status.Info, "subregions list", data.subregionsList.items)

      setSubRegionsList(data.subregionsList.items)

      setSubRegionsLoading(false)
      updatePanelStatus("SubRegions", { status: "success", loading: false })
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `subregionsList`, e.message)
      }
      setSubRegionsLoading(false)
      updatePanelStatus("SubRegions", { status: "error", loading: false })
    }
  }, [setError, setErrorMessage])

  // get countries list
  const getCountriesList = useCallback(async () => {
    updatePanelStatus("Countries", { loading: true })

    try {
      logger(Status.Api, "QUERY countriesList", listCountries)
      const { data } = await listCountriesQuery({
        variables: { input: { limit: 1000 } },
      })
      logger(Status.Info, "countries list", data.countriesList.items)

      setCountriesList(data.countriesList.items)

      setCountriesLoading(false)
      updatePanelStatus("Countries", { status: "success", loading: false })
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `countriesList`, e.message)
      }
      setCountriesLoading(false)
      updatePanelStatus("Countries", { status: "error", loading: false })
    }
  }, [setError, setErrorMessage])

  // add update function to panel status
  useEffect(() => {
    updatePanelStatus("Regions", { updateFunction: getRegionsList })
    updatePanelStatus("SubRegions", { updateFunction: getSubRegionsList })
    updatePanelStatus("Countries", { updateFunction: getCountriesList })
  }, [])

  // initial fetch
  useEffect(() => {
    getRegionsList()
    getSubRegionsList()
    getCountriesList()
  }, [])

  return (
    <CountriesContext.Provider
      value={{
        loading,
        regionsList,
        subRegionsList,
        countriesList,
      }}
    >
      {children}
    </CountriesContext.Provider>
  )
}

export { CountriesController, CountriesContext }
